import React from "react";
import { ItemGroup, ItemGroupSummary } from "../component";
import { ListDelimiter } from "./ListDelimiter";
import ErrorBoundary from "../../ErrorBoundary";
import { ForwardLink } from "./ForwardLink";
import ItemMedicationSummaryCard from "./ItemMedicationSummaryCard";
import MedicationCardAutoRefillLine from "./MedicationCardAutoRefillLine";
import MedicationCard, { CardStyle } from "./MedicationCard";
import ItemAnnotations from "./ItemAnnotations";
import { filterAnnotations } from "./AnnotationUtils";
import { generateUrlWithRefTag, PILL_PACK_REF_TAGS, REF_TAGS } from "../csa/utils";

interface GenerateOrderSummaryItemsProps {
    itemGroupSummary: ItemGroupSummary;
    isPillPackComDelivery?: boolean;
}

const GenerateOrderSummaryItems: React.FC<GenerateOrderSummaryItemsProps> = (props) => {
    const generatePillPackOrderSummary = () => {
        if (props.itemGroupSummary?.pillPackGroupSummary?.itemGroups?.length > 0) {
            const pillPackGroupSummary = props.itemGroupSummary.pillPackGroupSummary;
            const orderSummaryUrl = generateUrlWithRefTag(`/order/${pillPackGroupSummary?.id}`,
                PILL_PACK_REF_TAGS.TRACKER_TO_SUMMARY);
            return <ErrorBoundary>
                <ForwardLink url={orderSummaryUrl} csaId={'pillpack-order'}>
                    <MedicationCard headline={pillPackGroupSummary.headline} subline={pillPackGroupSummary.subline}
                        isPillPack={true} />
                </ForwardLink>
                {pillPackGroupSummary?.annotations &&
                    <ItemAnnotations annotations={filterAnnotations(pillPackGroupSummary?.annotations,
                        new Set<string>(["Refund"]))} />}
            </ErrorBoundary>
        }
        return null;
    }

    const generateOrderSummaryItem = (i: number, group: ItemGroup, isBottle?: boolean) => {
        const orderSummaryUrl = generateUrlWithRefTag(`/order/${group.id}`,
            isBottle === true ? PILL_PACK_REF_TAGS.TRACKER_TO_SUMMARY : REF_TAGS.TRACKER_TO_SUMMARY);
        return <React.Fragment key={i}>{group.itemSummaries.map((summary, j) => {
            // Ignore AR line for PillPack.com orders by checking boolean passed by the backend
            // Let's default to true to avoid messing with existing experience if we have null cases.
            const isNotPillPackCom = props.isPillPackComDelivery != null
                ? !props.isPillPackComDelivery
                : true;
            const autoRefillCardLine = <MedicationCardAutoRefillLine
                isAutoRefill={summary.isAutoRefill && isNotPillPackCom}
                autoRefillTextDisplay={props.itemGroupSummary.orderingChannelLine} />;
            return (<React.Fragment key={j}>
                <ErrorBoundary>
                    <ForwardLink url={orderSummaryUrl} csaId='order'>
                        <ItemMedicationSummaryCard
                            purchaseItemSummary={summary}
                            showSupply={true}
                            autoRefillCardLine={autoRefillCardLine}
                            cardStyle={CardStyle.HEADLINE_MED_50PX_IMG}
                        />
                    </ForwardLink>
                </ErrorBoundary>
            </React.Fragment>);
        }
        )}</React.Fragment>;
    }

    const bottleMedsLength = props.itemGroupSummary?.bottleGroupSummary?.itemGroups?.length;
    return (
        <ListDelimiter delimiter={<pui-divider spacingTop="small" spacingBottom="medium" />}>
            {generatePillPackOrderSummary()}
            {bottleMedsLength > 0 && props.itemGroupSummary.bottleGroupSummary.itemGroups.map((group, i) =>
                generateOrderSummaryItem(i, group, true)
            )}
            {props.itemGroupSummary.itemGroups.map((group, i) =>
                generateOrderSummaryItem(bottleMedsLength ? i + bottleMedsLength : i, group)
            )}
        </ListDelimiter>
    );
};

export default GenerateOrderSummaryItems;
